<script setup lang="ts">
import { useDeckPlayerContext } from '../../composables/useDeckPlayerShell'
import { vimeoEmbedUrl, youtubeEmbedUrl, type ResolvedMedia } from '../../flowchart/runtime/media'

/** Two media panes side by side in place of the card (split mode). */
defineProps<{ resolvedMedia: ResolvedMedia }>()

const { nowPlaying } = useDeckPlayerContext()
const {
  initYoutube: initialiseYoutubePlayer,
  initVimeo: initialiseVimeoPlayer,
  logNativeEvent: logNativeMediaEvent,
  onNativeEnded: handleNativeMediaEnded,
} = nowPlaying
</script>

<template>
  <div class="pfc-media-inline pfc-media-split-inline">
    <div class="pfc-media-split-inline__panes">
      <div
        v-for="(pane, pi) in [
          { url: resolvedMedia.leftUrl, type: resolvedMedia.leftType },
          { url: resolvedMedia.rightUrl, type: resolvedMedia.rightType },
        ]"
        :key="pi"
        class="pfc-media-split-inline__pane"
      >
        <img
          v-if="pane.type === 'image'"
          :src="pane.url || undefined"
          class="pfc-media-img pfc-media-img--fill"
        />
        <template v-else-if="pane.type === 'video'">
          <iframe
            v-if="youtubeEmbedUrl(pane.url)"
            :src="youtubeEmbedUrl(pane.url)!"
            class="pfc-media-video"
            allow="
              accelerometer;
              autoplay;
              clipboard-write;
              encrypted-media;
              gyroscope;
              picture-in-picture;
            "
            allowfullscreen
            @load="initialiseYoutubePlayer"
          />
          <iframe
            v-else-if="vimeoEmbedUrl(pane.url)"
            :src="vimeoEmbedUrl(pane.url)!"
            class="pfc-media-video"
            allow="
              accelerometer;
              autoplay;
              clipboard-write;
              encrypted-media;
              gyroscope;
              picture-in-picture;
            "
            allowfullscreen
            @load="initialiseVimeoPlayer"
          />
          <video
            v-else
            :src="pane.url || undefined"
            controls
            class="pfc-media-video"
            @play="logNativeMediaEvent"
            @pause="logNativeMediaEvent"
            @seeking="logNativeMediaEvent"
            @seeked="logNativeMediaEvent"
            @ratechange="logNativeMediaEvent"
            @ended="handleNativeMediaEnded"
          />
        </template>
        <div v-else-if="pane.type === 'audio'" class="pfc-media-audio-wrap">
          <audio
            :src="pane.url || undefined"
            controls
            class="pfc-media-audio"
            @play="logNativeMediaEvent"
            @pause="logNativeMediaEvent"
            @seeking="logNativeMediaEvent"
            @seeked="logNativeMediaEvent"
            @ratechange="logNativeMediaEvent"
            @ended="handleNativeMediaEnded"
          />
        </div>
        <iframe
          v-else-if="pane.type === 'pdf'"
          :src="pane.url || undefined"
          class="pfc-media-pdf"
        />
        <div v-else class="pfc-media-placeholder">—</div>
      </div>
    </div>
  </div>
</template>

<style scoped src="./styles/playerMedia.css"></style>
